import { client as request } from "./request";
import { Staff, Office, Company } from "./company-api";
import Duration from "@/models/duration";
import parseDate from "@/helpers/parseDate";
import { isAfter, endOfDay } from "date-fns";

/**
 * 他社工程
 */
export interface OtherCompanyConstruction {
  id: string;
  serviceTaskTypeId: number;
  name: string;
  shortName: string;
  mConstructionId: number;
}

/**
 * 他工程
 */
export interface ConstructionTask {
  id: string;
  serviceTaskTypeId: number;
  name: string;
  shortName: string;
  mConstructionId: number;
  date: Date;
}

/**
 * 工程行
 */
export interface TaskGroup {
  taskGroupId: any;
  projectId: any;
  name: string;
  projectMemberId: any;
  serviceTaskTypeId?: any;
  officeId: any;
  taskTypeId: any;
  order: number;
  tasks: Task[];
  taskType: string;
  staff?: Staff;
  office?: Office;
  company?: Company;
}

/**
 * (日時を与え)具体化した工程プラン
 */
export interface ConcretizedPlan {
  projectId: string;
  templateId: string;
  startDate: string;
}

/**
 * 各工程
 */
export interface Task {
  taskId: any;
  taskGroupId: any;
  name: string;
  start: Date;
  end: Date;
  duration: number;
  closedDays: number[]; // 休工日
  created: Date;
  subTasks: SubTask[];
}

export interface ServiceSchedule {
  taskId: any;
  taskGroupId: any;
  name: string;
  start: Date;
  end: Date;
  duration: number;
  closedDays: number[]; // 休工日
  created: Date;
  subTasks: SubTask[];
  projectId: any;
  projectName: string;
  projectStaff: string;
  memoCheck: string;
  memo: string;
  cliantName: string;
  cliantStaff: string;
  otherCompanyConstruction: OtherCompanyConstruction[];
  constructionTaskStatus: string;
  constructionTask: ConstructionTask[];
}

/**
 * 工程のチェックリスト
 */
export interface SubTask {
  taskId: any;
  name: string;
  done: boolean;
  created: Date;
  dueDate: string;
  behind: boolean;
  userName: string;
  date: string;
  time: string;
  users?: any;
}

function TaskGroup(data: any): TaskGroup {
  return Object.assign({}, data, {
    officeId: data.office ? data.office.officeId : "0",
    staffId: data.staff ? data.staff.staffId : "0",
    tasks: data.tasks.map(Task),
    taskType: data.taskType ? data.taskType.name : ""
  });
}

function Task(data: any): Task {
  const closedDays = String(data.closedDays)
    .split(",")
    .map(n => parseInt(n, 10))
    .filter(n => !isNaN(n));
  const start = new Date(data.start);
  const end = new Date(data.end);
  return Object.assign({}, data, {
    start,
    end,
    closedDays,
    created: new Date(data.created),
    subTasks: data.subTasks.map(SubTask)
  });
}

function SubTask(data: any): SubTask {
  return Object.assign({}, data, {
    behind: isSubTaskOverdue(data),
    created: new Date(data.created)
  });
}

/**
 * 期限を過ぎているかどうか
 */
function isSubTaskOverdue(data: any, baseDate: Date = new Date()): boolean {
  if (data.done || !data.dueDate) return false;
  const dueDate = endOfDay(parseDate(data.dueDate));
  return isAfter(baseDate, dueDate);
}

function withProjectId(projectId: any) {
  return (data: any) => Object.assign({}, data, { projectId });
}

/**
 * 工程表の情報を取得する
 * TaskGroup, Task, SubTaskをまとめて
 */
export async function getProjectTasks(projectId: any) {
  const res = await request.get("get_project_tasks.php", {
    projectId
  });
  return res.taskGroup.map(TaskGroup).map(withProjectId(projectId));
}

/**
 * 工程を取得する
 */
export async function getTask(taskId: number): Promise<Task | null> {
  const response = await request
    .get("get_project_task_detail.php", {
      id: taskId
    })
    .catch(() => null);
  return response && Task(response);
}

/**
 * 行を保存する
 */
export async function saveTaskGroup(data: FormData) {
  const res = await request.post("save_task_group.php", data);
  return res.taskGroupId;
}

/**
 * 空の行を追加する
 */
export async function addEmptyRow(projectId: any) {
  const data = new FormData();
  data.append("projectId", String(projectId));
  data.append("taskGroup[id]", "");
  data.append("taskGroup[taskTypeId]", "");
  data.append("taskGroup[officeId]", "");
  data.append("taskGroup[staffId]", "");
  data.append("taskGroup[order]", "");
  await saveTaskGroup(data);
}

/**
 * 行を削除する
 */
export async function deleteTaskGroup(taskGroupId: any) {
  await request.post("delete_task_group.php", {}, { taskGroupId });
}

/**
 * 工程を保存する
 * TaskとSubTaskをまとめて
 */
export async function saveTask(data: FormData) {
  const res = await request.post("save_task.php", data);
  return res.taskId;
}

/**
 * 日程を変更する
 * @param task 工程
 * @param start 開始日
 */
export function changeTaskSchedule(task: Task, start: Date) {
  return {
    ...task,
    ...Duration(start, task.duration)
  } as Task;
}

/**
 * 工程を削除する
 */
export async function deleteTask(taskId: any) {
  const data = new FormData();
  data.append("taskId", taskId);
  await request.post("delete_task.php", data);
}

export function newTask(taskGroupId: number, name: string, start: Date): Task {
  // デフォルトの休日
  const closedDays = [0];

  return {
    ...Duration(start, 5),
    taskGroupId,
    closedDays,
    taskId: "",
    name: name,
    created: new Date(),
    subTasks: []
  };
}

/**
 * 工程行を並び替える
 */
export async function sortTaskGroups(projectId: any, taskGroupIds: number[]) {
  const d = new FormData();
  d.append("projectId", String(projectId));
  taskGroupIds.forEach((id, i) => {
    d.append(`taskGroupIds[${i}]`, String(id));
  });
  await request.post("sort_task_groups.php", d);
}

export interface UpdateTaskDateCommand {
  id: number; // task id
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
  moveTogether?: boolean;
}

/**
 * 工程の日付を変更する
 */
export async function updateTaskDate(
  command: UpdateTaskDateCommand
): Promise<void> {
  const data = new FormData();
  data.append("id", String(command.id));
  data.append("startDate", command.startDate);
  data.append("endDate", command.endDate);
  if (command.moveTogether) {
    data.append("together", "1");
  }
  await request.post("save_task_date.php", data);
}

/**
 * 工程プランのタスクを工程表に追加する
 */
export async function addTasksFromConcretizedPlan(
  newTasks: ConcretizedPlan
): Promise<void> {
  const data = new FormData();
  data.append("projectId", newTasks.projectId);
  data.append("templateId", newTasks.templateId);
  data.append("startDate", newTasks.startDate);
  await request.post("add_template_tasks.php", data);
}
